import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

const TermsOfService = () => {
  return (
    <div className="pt-24 pb-16" data-testid="terms-of-service-page">
      <div className="max-w-4xl mx-auto px-4 md:px-8">
        {/* Back to Cart Link */}
        <Link 
          to="/cart" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
          data-testid="back-to-cart-link"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to cart
        </Link>

        {/* Header */}
        <div className="text-center mb-12">
          <p className="label-style mb-2">Legal</p>
          <h1 
            className="text-4xl md:text-5xl font-bold mb-4"
            style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
          >
            Terms and Conditions
          </h1>
        </div>

        <div className="space-y-8">
          {/* Terms and Conditions */}
          <div className="bg-white border border-border p-6 rounded-lg">
            <h2 
              className="font-bold text-xl mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              Terms and Conditions
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                All colours and parts are aftermarket and may differ from parts that come on vehicles as standard.
              </p>
              <p>
                The decals and badges on this website are not official (unless otherwise stated) and are only meant as compatible replacements or decorative items.
              </p>
              <p>
                Our parts are designed to be used as complete replacements for old and damaged parts.
              </p>
              <p>
                No guarantees are made to the colour and finish in comparison to OEM parts.
              </p>
              <p className="font-medium text-foreground">
                Please make an enquiry before ordering if you have specific needs.
              </p>
            </div>
          </div>

          {/* Made to Order Notice */}
          <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg">
            <h2 
              className="font-bold text-xl mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              All of our graphics and badges are made to order!
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                This means that there may be unforseen delays in sending orders. The delivery times given in checkout are an aim and not a guarantee.
              </p>
              <p>
                <strong>eg. Royal Mail Tracked</strong> - this is a 48 hour delivery from Royal Mail, however we often need 7+ working days for manufacture depending on our order queue and up to 14 working days in peak periods. This is subject to change without notice.
              </p>
              <p className="font-medium text-foreground">
                Please bare this in mind before ordering.
              </p>
              <p>
                Zen Graphics Limited will endeavour to meet any delivery date quoted but delivery dates and times given are intended as an estimate only. If there are any circumstances whatsoever which prevent or delay delivery, Zen Graphics Limited shall not be bound to supply or make delivery of any goods ordered nor shall it be liable for any damage or consequential loss or in any other way for failure to supply or delay in delivery when so prevented or delayed.
              </p>
              <p className="font-medium">
                All parts are aftermarket unless otherwise stated.
              </p>
            </div>
          </div>

          {/* Shipping Information */}
          <div className="bg-white border border-border p-6 rounded-lg">
            <h2 
              className="font-bold text-xl mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              Shipping Information
            </h2>
            
            <div className="space-y-6">
              {/* USA */}
              <div>
                <h3 className="font-bold mb-2">USA:</h3>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li>USPS Priority Tracked</li>
                  <li>UPS Express delivery</li>
                </ul>
              </div>

              {/* UK */}
              <div>
                <h3 className="font-bold mb-2">UK:</h3>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li>Royal Mail Tracked</li>
                </ul>
                <p className="text-muted-foreground mt-2">
                  Where your item is too large for Royal Mail Postage, a courier will be used from either UPS, Parcelforce or Hermes.
                </p>
              </div>

              {/* Europe */}
              <div>
                <h3 className="font-bold mb-2">Europe:</h3>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li>Tracked Europe Shipping - 5-14 day delivery</li>
                  <li>UPS EU Zone 1 - 4-7 day delivery</li>
                  <li>UPS EU Zone 2 - 5-8 day delivery</li>
                </ul>
              </div>

              {/* Worldwide */}
              <div>
                <h3 className="font-bold mb-2">Worldwide:</h3>
                <ul className="list-disc pl-6 space-y-1 text-muted-foreground">
                  <li>Priority post with tracking</li>
                  <li>5-14 day delivery Worldwide</li>
                </ul>
                <p className="text-muted-foreground mt-2">
                  Royal Mail uses delivery partners in the receiving country. For more information follow this link:{' '}
                  <a 
                    href="https://www.royalmail.com/sending/international/international-tracked-signed/delivery-partners" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    Royal Mail International Partners
                  </a>
                </p>
              </div>

              <p className="text-muted-foreground">
                Shipping is calculated in the checkout process. All services available for the item/s you wish to purchase will be shown during checkout. Discount codes may not apply to shipping costs.
              </p>
            </div>
          </div>

          {/* UPS Extended Areas */}
          <div className="bg-secondary/30 p-6 rounded-lg">
            <h3 
              className="font-bold text-lg mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              UPS Extended Delivery Areas
            </h3>
            <p className="text-muted-foreground mb-4">
              <strong>UPS:</strong> some areas accrue a rural delivery charge, this includes the Scottish Highlands. Some areas of Europe also fall into this extended area delivery charge and we may cancel or change the delivery method at our discretion based on your location.
            </p>
            <p className="text-muted-foreground mb-4">
              We are working to factor this area based charge into our pricing structure, however we do not have this facility at this time. If you would like to know if your area is within UPS extended delivery areas please see here:{' '}
              <a 
                href="https://www.ups.com/gb/en/shipping/zones-and-rates/area-surcharge.page" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                UPS Area Surcharges
              </a>
            </p>
            <p className="text-muted-foreground">
              Orders for bulkier items may be sent via courier at our discretion. We use Hermes, DPD, TNT, UPS and Parcelforce services. If your order is to be dispatched via this method you will receive an email from the courier.
            </p>
          </div>

          {/* Customs and Duty */}
          <div className="bg-white border border-border p-6 rounded-lg">
            <h2 
              className="font-bold text-xl mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              Customs and Duty Information
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p className="font-medium text-foreground">
                It is the purchaser's / importer's sole responsibility for import taxes.
              </p>
              <p>
                If your order is delivered outside the UK, it may be subject to import taxes, customs duties and charges in the destination country, payable by the customer. Zen Graphics Limited has no responsibility for these charges.
              </p>
              <p>
                <strong>Customs and Duty for imports:</strong> Local duty or customs taxes may be levied by the purchaser's country if you are located in Switzerland, Norway, North America, South America, Africa, Asia or Oceania.
              </p>
              <p>
                It is the purchaser's / importer's sole responsibility for import taxes. These will usually be levied by the shipping company.
              </p>
            </div>
          </div>

          {/* Brexit */}
          <div className="bg-blue-50 border border-blue-200 p-6 rounded-lg">
            <h2 
              className="font-bold text-xl mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              Brexit
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                Brexit happened on January 1st 2021. From this date all sales to EU member nations may be subject to import fees. Here we have a simple guide for what to expect:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>If applicable to your order, these fees are automatically calculated by the shipping company.</li>
                <li>There are no delays with delivery. Shipping companies pay upfront to customs in your country and then bill you later if any fees apply.</li>
                <li>If you return your goods you can apply for a refund from the shipping company.</li>
                <li>Please check your country's own VAT and customs import duty so you are aware if any fees will be payable.</li>
              </ul>
              <p>
                We hope to make this transition easy and understandable for all of our customers. If you have any questions please do not hesitate to contact us.
              </p>
            </div>
          </div>

          {/* Returns Policy */}
          <div className="bg-white border border-border p-6 rounded-lg">
            <h2 
              className="font-bold text-xl mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              Returns Policy
            </h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                <strong>Returns are accepted if reported within 30 days of receipt of the item.</strong>
              </p>
              <p>
                For items customised by size or colour, there is a 20% restocking fee on the returned item.
              </p>
              <p>
                For your own peace of mind, we recommend using a tracked or signed for service.
              </p>
              <div className="bg-secondary/50 p-4 rounded mt-4">
                <p className="font-medium mb-2">Please send all returns to:</p>
                <address className="not-italic">
                  Zen Graphics Ltd<br />
                  37 Mount Pleasant<br />
                  Twogates<br />
                  Tamworth<br />
                  Staffs<br />
                  B77 1HL<br />
                  United Kingdom
                </address>
              </div>
              <p className="text-sm italic">
                All returns should be carefully packaged and a letter should be enclosed with your name and order number attached for processing. Returns without this information will be delayed or may not be processed if the origins cannot be found.
              </p>
            </div>
          </div>

          {/* Privacy Policy */}
          <div className="bg-white border border-border p-6 rounded-lg">
            <h2 
              className="font-bold text-xl mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              Privacy Policy
            </h2>
            <p className="text-sm text-muted-foreground mb-4">We are ICO certified.</p>
            <div className="space-y-4 text-muted-foreground">
              <p>
                This privacy policy has been compiled to better serve those who are concerned with how their 'Personally Identifiable Information' (PII) is being used online. PII, as described in UK privacy law and information security, is information that can be used on its own or with other information to identify, contact, or locate a single person, or to identify an individual in context. Please read our privacy policy carefully to get a clear understanding of how we collect, use, protect or otherwise handle your Personally Identifiable Information in accordance with our website.
              </p>
            </div>
          </div>

          {/* Information Collection */}
          <div className="bg-secondary/30 p-6 rounded-lg">
            <h3 
              className="font-bold text-lg mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              What personal information do we collect?
            </h3>
            <p className="text-muted-foreground mb-4">
              When ordering or registering on our site, as appropriate, you may be asked to enter your name, email address or other details to help you with your experience.
            </p>
            
            <h4 className="font-medium mb-2">When do we collect information?</h4>
            <p className="text-muted-foreground">
              We collect information from you when you register on our site, place an order, subscribe to a newsletter or enter information on our site.
            </p>
          </div>

          {/* How We Use Information */}
          <div className="bg-white border border-border p-6 rounded-lg">
            <h3 
              className="font-bold text-lg mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              How do we use your information?
            </h3>
            <p className="text-muted-foreground mb-4">
              We may use the information we collect from you when you register, make a purchase, sign up for our newsletter, respond to a survey or marketing communication, surf the website, or use certain other site features in the following ways:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>To personalize your experience and to allow us to deliver the type of content and product offerings in which you are most interested.</li>
              <li>To improve our website in order to better serve you.</li>
              <li>To allow us to better service you in responding to your customer service requests.</li>
              <li>To quickly process your transactions.</li>
            </ul>
          </div>

          {/* How We Protect Information */}
          <div className="bg-white border border-border p-6 rounded-lg">
            <h3 
              className="font-bold text-lg mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              How do we protect your information?
            </h3>
            <div className="space-y-4 text-muted-foreground">
              <p>
                Our website is scanned on a regular basis for security holes and known vulnerabilities in order to make your visit to our site as safe as possible.
              </p>
              <p>We use regular Malware Scanning.</p>
              <p>
                Your personal information is contained behind secured networks and is only accessible by a limited number of persons who have special access rights to such systems, and are required to keep the information confidential. In addition, all sensitive/credit information you supply is encrypted via Secure Socket Layer (SSL) technology.
              </p>
              <p>
                We implement a variety of security measures when a user places an order enters, submits, or accesses their information to maintain the safety of your personal information.
              </p>
              <p className="font-medium">
                All transactions are processed through a gateway provider and are not stored or processed on our servers.
              </p>
            </div>
          </div>

          {/* Cookies */}
          <div className="bg-secondary/30 p-6 rounded-lg">
            <h3 
              className="font-bold text-lg mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              Do we use 'cookies'?
            </h3>
            <div className="space-y-4 text-muted-foreground">
              <p>We do not use cookies for tracking purposes.</p>
              <p>
                You can choose to have your computer warn you each time a cookie is being sent, or you can choose to turn off all cookies. You do this through your browser settings. Since each browser is a little different, look at your browser's Help Menu to learn the correct way to modify your cookies.
              </p>
              <p>
                If you turn cookies off, some of the features that make your site experience more efficient may not function properly.
              </p>
            </div>
          </div>

          {/* Third Party */}
          <div className="bg-white border border-border p-6 rounded-lg">
            <h3 
              className="font-bold text-lg mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              Third-party disclosure
            </h3>
            <p className="text-muted-foreground mb-4">
              We do not sell, trade, or otherwise transfer to outside parties your Personally Identifiable Information.
            </p>
            
            <h4 className="font-medium mb-2">Third-party links</h4>
            <p className="text-muted-foreground mb-4">
              We do not include or offer third-party products or services on our website.
            </p>

            <h4 className="font-medium mb-2">Google</h4>
            <p className="text-muted-foreground">
              Google's advertising requirements can be summed up by Google's Advertising Principles. They are put in place to provide a positive experience for users.{' '}
              <a 
                href="https://support.google.com/adwordspolicy/answer/1316548?hl=en" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Learn more
              </a>
            </p>
            <p className="text-muted-foreground mt-2">
              We have not enabled Google AdSense on our site but we may do so in the future.
            </p>
          </div>

          {/* User Rights */}
          <div className="bg-white border border-border p-6 rounded-lg">
            <h3 
              className="font-bold text-lg mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              We agree to the following:
            </h3>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Users can visit our site anonymously.</li>
              <li>Once this privacy policy is created, we will add a link to it on our home page or as a minimum, on the first significant page after entering our website.</li>
              <li>Our Privacy Policy link includes the word 'Privacy' and can easily be found on the page specified above.</li>
            </ul>
            
            <h4 className="font-medium mt-4 mb-2">You will be notified of any Privacy Policy changes:</h4>
            <p className="text-muted-foreground">On our Privacy Policy Page</p>
            
            <h4 className="font-medium mt-4 mb-2">You can change your personal information:</h4>
            <p className="text-muted-foreground">By logging in to your account</p>
          </div>

          {/* Do Not Track */}
          <div className="bg-secondary/30 p-6 rounded-lg">
            <h3 
              className="font-bold text-lg mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              How does our site handle Do Not Track signals?
            </h3>
            <p className="text-muted-foreground mb-4">
              We honor Do Not Track signals and Do Not Track, plant cookies, or use advertising when a Do Not Track (DNT) browser mechanism is in place.
            </p>
            
            <h4 className="font-medium mb-2">Does our site allow third-party behavioral tracking?</h4>
            <p className="text-muted-foreground">
              It's also important to note that we do not allow third-party behavioral tracking.
            </p>
          </div>

          {/* COPPA */}
          <div className="bg-white border border-border p-6 rounded-lg">
            <h3 
              className="font-bold text-lg mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              COPPA (Children Online Privacy Protection Act)
            </h3>
            <p className="text-muted-foreground mb-4">
              When it comes to the collection of personal information from children under the age of 13 years old, the Children's Online Privacy Protection Act (COPPA) puts parents in control. The Federal Trade Commission, consumer protection agency, enforces the COPPA Rule, which spells out what operators of websites and online services must do to protect children's privacy and safety online.
            </p>
            <p className="text-muted-foreground">
              We do not specifically market to children under the age of 13 years old.
            </p>
          </div>

          {/* Fair Information Practices */}
          <div className="bg-white border border-border p-6 rounded-lg">
            <h3 
              className="font-bold text-lg mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              Fair Information Practices
            </h3>
            <p className="text-muted-foreground mb-4">
              The Fair Information Practices Principles form the backbone of privacy law and the concepts they include have played a significant role in the development of data protection laws around the globe. Understanding the Fair Information Practice Principles and how they should be implemented is critical to comply with the various privacy laws that protect personal information.
            </p>
            <p className="text-muted-foreground mb-4">
              In order to be in line with Fair Information Practices we will take the following responsive action, should a data breach occur:
            </p>
            <p className="text-muted-foreground font-medium">
              We will notify you via email within 7 business days.
            </p>
            <p className="text-muted-foreground mt-4">
              We also agree to the Individual Redress Principle which requires that individuals have the right to legally pursue enforceable rights against data collectors and processors who fail to adhere to the law. This principle requires not only that individuals have enforceable rights against data users, but also that individuals have recourse to courts or government agencies to investigate and/or prosecute non-compliance by data processors.
            </p>
          </div>

          {/* CAN SPAM Act */}
          <div className="bg-white border border-border p-6 rounded-lg">
            <h3 
              className="font-bold text-lg mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              CAN SPAM Act
            </h3>
            <p className="text-muted-foreground mb-4">
              The CAN-SPAM Act is a law that sets the rules for commercial email, establishes requirements for commercial messages, gives recipients the right to have emails stopped from being sent to them, and spells out tough penalties for violations.
            </p>
            
            <h4 className="font-medium mb-2">We collect your email address in order to:</h4>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground mb-4">
              <li>Send information, respond to inquiries, and/or other requests or questions</li>
              <li>Process orders and to send information and updates pertaining to orders</li>
              <li>Send you additional information related to your product and/or service</li>
            </ul>

            <h4 className="font-medium mb-2">To be in accordance with CAN-SPAM, we agree to the following:</h4>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Not use false or misleading subjects or email addresses</li>
              <li>Identify the message as an advertisement in some reasonable way</li>
              <li>Include the physical address of our business or site headquarters</li>
              <li>Monitor third-party email marketing services for compliance, if one is used</li>
              <li>Honor opt-out/unsubscribe requests quickly</li>
              <li>Allow users to unsubscribe by using the link at the bottom of each email</li>
            </ul>
          </div>

          {/* Unsubscribe */}
          <div className="bg-yellow-50 border border-yellow-200 p-6 rounded-lg">
            <h3 
              className="font-bold text-lg mb-4"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              Unsubscribe
            </h3>
            <p className="text-muted-foreground">
              If at any time you would like to unsubscribe from receiving future emails, you can email us at{' '}
              <a href="mailto:sales@zengraphics.co.uk" className="text-primary hover:underline">
                sales@zengraphics.co.uk
              </a>
              {' '}and we will promptly remove you from ALL correspondence.
            </p>
          </div>

          {/* Contact */}
          <div className="text-center bg-secondary/30 p-6 rounded-lg">
            <h3 
              className="font-bold text-lg mb-2"
              style={{ fontFamily: 'Calibri, Carlito, "Segoe UI", sans-serif' }}
            >
              Contacting Us
            </h3>
            <p className="text-muted-foreground mb-4">
              If there are any questions regarding this privacy policy, you may contact us using the information below.
            </p>
            <p className="mb-2">shop.zengraphics.co.uk</p>
            <a 
              href="mailto:sales@zengraphics.co.uk" 
              className="text-primary hover:underline text-lg"
            >
              sales@zengraphics.co.uk
            </a>
            <p className="text-sm text-muted-foreground mt-4">
              Last Edited on 2024-04-22
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
